import NotaFiscalSaidaHistoricoFiscal from "./NotaFiscalSaidaHistoricoFiscal";
import AuthContext from "contexts/AuthContext";
import Base from "components/Base";
import { useToastContext } from "components/alert/ToastContext";
import { isAxiosError } from "axios";
import { saveAs } from "file-saver";
import NotaFiscalSaida, { NotaFiscalSaidaItem, NotaFiscalSaidaParcela } from "model/vendas/NotaFiscalSaida";
import NotaFiscalSaidaDocumentos from "./NotaFiscalSaidaDocumentos";
import NotaFiscalSaidaEmails from "./NotaFiscalSaidaEmails";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotaFiscalSaidaService from "service/vendas/NotaFiscalSaidaService";
import { converterData } from "utils/convert";

const service = new NotaFiscalSaidaService();
const moeda = (valor: number | null | undefined) => valor == null ? "—" : valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const data = (valor: string | null) => valor ? new Date(valor).toLocaleString("pt-BR") : "—";

export default function NotaFiscalSaidaConsulta() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { acoes } = useContext(AuthContext);
    const podeAtualizarDados = acoes.some(acao => acao.sigla === "NOTA_FISCAL_SAIDA_GERENCIAR");
    const { msgErro, msgSucesso } = useToastContext();
    const [nota, setNota] = useState<NotaFiscalSaida | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [excluindo, setExcluindo] = useState(false);
    const podeExcluir = podeAtualizarDados && nota !== null
        && ["REJEITADA", "FALHA_PREPARACAO"].includes(nota.status)
        && !nota.estoqueMovimentado && !nota.protocoloAutorizacao && !nota.arquivada;
    const [consultando, setConsultando] = useState(false);
    const [baixando, setBaixando] = useState<"xml" | "danfe" | null>(null);
    const [cancelando, setCancelando] = useState(false);
    const [dialogoCancelamento, setDialogoCancelamento] = useState(false);
    const [motivoCancelamento, setMotivoCancelamento] = useState("");
    const [erroCancelamento, setErroCancelamento] = useState<string | null>(null);
    const podeCancelar = acoes.some(a=>a.sigla==="NOTA_FISCAL_SAIDA_CANCELAR") && nota !== null
        && ["AUTORIZADA", "AUTORIZADA_ESTOQUE_PENDENTE"].includes(nota.status);
    const motivoValido = motivoCancelamento.trim().length >= 15 && motivoCancelamento.trim().length <= 255;

    function abrirCancelamento() {
        if (!podeCancelar || cancelando || consultando || excluindo || baixando) return;
        setMotivoCancelamento("");
        setErroCancelamento(null);
        setDialogoCancelamento(true);
    }

    async function cancelarNota() {
        if (!nota || !podeCancelar || !motivoValido || cancelando || consultando || excluindo || baixando) return;
        const idNota = nota.id;
        setCancelando(true);
        setErroCancelamento(null);
        try {
            const resultado = await service.cancelar(idNota, { motivo: motivoCancelamento.trim() });
            setNota(anterior => anterior?.id === idNota ? { ...anterior, statusProcessamento: resultado.status } : anterior);
            setDialogoCancelamento(false);
            msgSucesso("Cancelamento da nota fiscal", resultado.mensagem);
            try {
                const atualizada = await service.consultar(idNota);
                setNota(anterior => anterior?.id === idNota ? atualizada : anterior);
            } catch {
                msgErro("Consulta da nota fiscal", "Pedido de cancelamento enviado. Atualize a situação para acompanhar a confirmação.");
            }
        } catch (error) {
            let mensagem = "Não foi possível solicitar o cancelamento da nota fiscal.";
            if (isAxiosError<{ detail?: string; message?: string }>(error)) {
                const detalhe = error.response?.data?.detail || error.response?.data?.message;
                if (detalhe) mensagem = detalhe;
                else if (error.response?.status === 422) mensagem = "A SEFAZ rejeitou o cancelamento. Verifique o prazo e a situação da nota.";
                else if (error.response?.status === 409) mensagem = "A nota não está disponível para cancelamento. Atualize a situação.";
                if (!error.response || error.response.status >= 500) {
                    setNota(anterior => anterior?.id === idNota ? { ...anterior, statusProcessamento: "CANCELAMENTO_INDETERMINADO" } : anterior);
                    mensagem += " Consulte a situação antes de tentar novamente.";
                }
            }
            setErroCancelamento(mensagem);
            msgErro("Cancelamento da nota fiscal", mensagem);
            try {
                const atualizada = await service.consultar(idNota);
                setNota(anterior => anterior?.id === idNota ? atualizada : anterior);
            } catch {
                // Mantém a situação conhecida até que a consulta possa ser concluída.
            }
        } finally {
            setCancelando(false);
        }
    }

    useEffect(() => {
        let ativo = true;
        const numeroId = Number(id);
        if (!Number.isSafeInteger(numeroId) || numeroId <= 0) {
            setCarregando(false);
            return;
        }
        service.consultar(numeroId)
            .then(resultado => { if (ativo) setNota(resultado); })
            .catch(() => { if (ativo) msgErro("Nota fiscal de saída", "Não foi possível carregar a nota."); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [id]);

    useEffect(() => {
        if (!nota || !(["AGUARDANDO_SEFAZ", "SEFAZ_INDETERMINADA"].includes(nota.status) || ["CANCELAMENTO_PENDENTE","CANCELAMENTO_INDETERMINADO"].includes(nota.statusProcessamento??""))) return;
        let ativo = true;
        let buscando = false;
        const idNota = nota.id;
        const intervalo = window.setInterval(async () => {
            if (buscando) return;
            buscando = true;
            try {
                const atualizada = await service.consultar(idNota);
                if (ativo) setNota(atualizada);
            } catch {
                // Preserva a última situação conhecida quando a API está indisponível.
            } finally {
                buscando = false;
            }
        }, 5000);
        return () => { ativo = false; window.clearInterval(intervalo); };
    }, [nota?.id, nota?.status, nota?.statusProcessamento]);

    async function atualizarSituacao() {
        if (!nota || consultando || excluindo || cancelando) return;
        setConsultando(true);
        try {
            await service.consultarSituacao(nota.id);
            setNota(await service.consultar(nota.id));
            msgSucesso("Nota fiscal de saída", "Consulta SEFAZ agendada. Atualize a nota para acompanhar o resultado.");
        } catch {
            msgErro("Nota fiscal de saída", "Não foi possível atualizar a situação da nota.");
        } finally {
            setConsultando(false);
        }
    }

    async function excluirNota() {
        if (!nota || !podeExcluir || excluindo || consultando || baixando || cancelando) return;
        setExcluindo(true);
        try {
            await service.excluir(nota.id);
            msgSucesso("Nota fiscal de saída", "Nota fiscal excluída.");
            navigate("/notaFiscalSaida");
        } catch (error) {
            let mensagem = "Não foi possível excluir a nota fiscal.";
            if (isAxiosError<{ detail?: string }>(error)) {
                if (error.response?.data?.detail) mensagem = error.response.data.detail;
                else if (error.response?.status === 409) {
                    mensagem = "Somente notas rejeitadas ou com falha no envio, sem autorização ou movimentação de estoque, podem ser excluídas.";
                }
            }
            msgErro("Nota fiscal de saída", mensagem);
        } finally {
            setExcluindo(false);
        }
    }

    function confirmarExclusao() {
        if (!nota || !podeExcluir || excluindo || consultando || baixando || cancelando) return;
        confirmDialog({
            header: "Arquivar nota fiscal",
            message: `Excluir a nota fiscal ${nota.numeroNota ?? nota.id} e seus itens? Esta ação não pode ser desfeita.`,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Arquivar", rejectLabel: "Voltar", acceptClassName: "p-button-danger",
            accept: () => { void excluirNota(); }
        });
    }

    async function baixarDocumento(tipo: "xml" | "danfe") {
        if (!nota || baixando || excluindo || cancelando) return;
        setBaixando(tipo);
        try {
            const arquivo = tipo === "xml" ? await service.baixarXml(nota.id) : await service.baixarDanfe(nota.id);
            saveAs(arquivo, `${tipo === "xml" ? "nfe" : "danfe"}-${nota.chaveAcesso || nota.id}.${tipo === "xml" ? "xml" : "pdf"}`);
        } catch (error) {
            let mensagem = "Não foi possível baixar o documento fiscal.";
            if (isAxiosError(error) && error.response?.status === 409) {
                mensagem = "O documento fiscal está indisponível ou sua integridade não foi confirmada.";
            } else if (isAxiosError(error) && error.response?.status === 404) {
                mensagem = "Documento fiscal não encontrado. Para XMLs gerados ou assinados, consulte a aba Documentos XML.";
            }
            msgErro(tipo === "xml" ? "XML da NF-e" : "DANFE", mensagem);
        } finally {
            setBaixando(null);
        }
    }

    return <Base tituloPagina="Consulta de nota fiscal de saída">
        <Dialog header="Cancelar nota fiscal" visible={dialogoCancelamento} modal
            style={{ width: "min(560px, 95vw)" }} closable={!cancelando} closeOnEscape={!cancelando}
            onHide={() => { if (!cancelando) setDialogoCancelamento(false); }}
            footer={<>
                <Button label="Voltar" icon="pi pi-arrow-left" outlined disabled={cancelando}
                    onClick={() => setDialogoCancelamento(false)} />
                <Button label="Solicitar cancelamento" icon="pi pi-ban" severity="danger" loading={cancelando}
                    disabled={!motivoValido || !podeCancelar || consultando || excluindo || baixando !== null}
                    onClick={() => { void cancelarNota(); }} />
            </>}>
            <p>Solicitar o cancelamento da nota fiscal {nota?.numeroNota ?? nota?.id}?
                A confirmação será recebida após o processamento na SEFAZ.</p>
            <label htmlFor="motivoCancelamento"><strong>Motivo do cancelamento</strong></label>
            <InputTextarea id="motivoCancelamento" value={motivoCancelamento} rows={5} maxLength={255}
                autoFocus disabled={cancelando} className="w-100 mt-2" aria-describedby="limiteMotivoCancelamento"
                onChange={e => setMotivoCancelamento(e.target.value)} />
            <small id="limiteMotivoCancelamento">Informe de 15 a 255 caracteres ({motivoCancelamento.trim().length}/255).</small>
            {erroCancelamento && <p className="text-danger" role="alert">{erroCancelamento}</p>}
        </Dialog>
        <div className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <h2>Nota fiscal de saída {nota?.numeroNota}</h2>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button label="Voltar" icon="pi pi-arrow-left" outlined onClick={() => navigate("/notaFiscalSaida")} />
                    {podeAtualizarDados && nota && <Button label="Duplicar" icon="pi pi-copy" outlined
                        disabled={consultando || excluindo || cancelando || baixando !== null}
                        onClick={() => navigate(`/notaFiscalSaida/emitir?duplicar=${nota.id}`)} />}
                    <Button type="button" label="Documentação" icon="pi pi-book" outlined
                        onClick={() => window.open('https://empxdocs.appsotecnologia.com.br/vendas/consulta-nota-fiscal', '_blank', 'noopener,noreferrer')} />
                    {nota && <Button label="Atualizar situação" icon="pi pi-refresh" loading={consultando}
                        disabled={consultando || !nota.chaveAcesso} onClick={atualizarSituacao} />}

                    {acoes.some(a=>a.sigla==="NOTA_FISCAL_SAIDA_CORRIGIR") && nota?.modelo === "NFE" && <Button label="Carta de correção" icon="pi pi-file-edit" outlined
                        disabled={consultando || excluindo || cancelando || baixando !== null}
                        onClick={() => navigate(`/notaFiscalSaida/cartaCorrecao/${nota.id}`)} />}
                    {podeCancelar && <Button label="Cancelar nota" icon="pi pi-ban" severity="danger" outlined
                        disabled={cancelando || consultando || excluindo || baixando !== null || !nota?.chaveAcesso}
                        onClick={abrirCancelamento} />}
                    {podeExcluir && <Button label="Editar e reenviar" icon="pi pi-pencil" outlined
                        disabled={consultando || excluindo || cancelando || baixando !== null}
                        onClick={() => navigate(`/notaFiscalSaida/editar/${nota!.id}`)} />}
                    {podeExcluir && <Button label="Arquivar nota" icon="pi pi-trash" severity="danger" outlined
                        loading={excluindo} disabled={consultando || cancelando || baixando !== null}
                        onClick={confirmarExclusao} />}
                </div>
            </div>
            {carregando && <p>Carregando...</p>}
            {!carregando && !nota && <p>Nota fiscal não encontrada.</p>}
            {nota && <>
                <p className="alert alert-info" role="status">A consulta agenda a conciliação na SEFAZ. Os dados abaixo mostram o último resultado confirmado pelo Empx; atualize os dados locais para acompanhar o processamento.</p>
                {["CANCELAMENTO_PENDENTE", "CANCELAMENTO_INDETERMINADO"].includes(nota.statusProcessamento??"") &&
                    <p role="status">{nota.statusProcessamento === "CANCELAMENTO_PENDENTE"
                        ? "Cancelamento solicitado. Atualize a situação para acompanhar a confirmação."
                        : "O resultado do cancelamento ainda não foi confirmado. Atualize a situação antes de tentar novamente."}</p>}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginBottom: 20 }}>
                    <div><strong>Situação fiscal:</strong> {nota.status.replaceAll("_", " ")}</div>
                    <div><strong>Processamento:</strong> {(nota.statusProcessamento??"—").replaceAll("_", " ")}</div>
                    <div><strong>Autorizador:</strong> {nota.autorizador??"—"}</div>
                    <div><strong>Série / número:</strong> {nota.numeroSerie ?? "—"} / {nota.numeroNota ?? "—"}</div>
                    <div><strong>Emissão:</strong> {data(nota.dataEmissao)}</div>
                    <div><strong>Destinatário:</strong> {nota.nomeDestinatario || "—"}</div>
                    <div><strong>Documento:</strong> {nota.numeroDocumentoDestinatario || "—"}</div>
                    <div><strong>Telefone do destinatário:</strong> {nota.telefoneDestinatario || "—"}</div>
                    <div><strong>Natureza:</strong> {nota.naturezaOperacao}</div>
                    <div><strong>Total:</strong> {moeda(nota.valorTotalNota)}</div>
                    <div><strong>Estoque movimentado:</strong> {nota.estoqueMovimentado ? "Sim" : "Não"}</div>
                    <div><strong>cStat:</strong> {nota.codigoStatusSefaz ?? "—"}</div>
                    <div><strong>Protocolo:</strong> {nota.protocoloAutorizacao || "—"}</div>
                </div>
                {nota.motivoSefaz && <p><strong>Mensagem da SEFAZ:</strong> {nota.motivoSefaz}</p>}
                <p><strong>Chave de acesso:</strong> {nota.chaveAcesso || "—"}</p>
                <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    <Button label="Baixar DANFE" icon="pi pi-file-pdf" outlined loading={baixando === "danfe"}
                        disabled={baixando !== null || excluindo || cancelando} onClick={() => { void baixarDocumento("danfe"); }} />
                    <Button label="Exportar XML e eventos" icon="pi pi-download" outlined disabled={baixando!==null} onClick={()=>{if(nota)service.exportarXml([nota.id]).then(b=>saveAs(b,`nfe-${nota.id}-eventos.zip`)).catch(()=>msgErro("Exportação","Não foi possível exportar os XMLs."));}}/>
                    <Button label="Baixar XML" icon="pi pi-file" outlined loading={baixando === "xml"}
                        disabled={baixando !== null || excluindo || cancelando} onClick={() => { void baixarDocumento("xml"); }} />
                </div>
                <TabView>
                    <TabPanel header="Tributos">
                        <div className="d-flex flex-wrap gap-3 mb-3">
                            <div><strong>IRPJ na emissão:</strong> {nota.regimeApuracaoIrpjEmitente?.replaceAll("_", " ") ?? "Não registrado"}</div>
                            <div><strong>PIS/COFINS na emissão:</strong> {nota.regimePisCofinsEmitente?.replaceAll("_", " ") ?? "Não registrado"}</div>
                        </div>
                        <p>Valores ausentes indicam que o resultado tributário não foi registrado. Não representam imposto zero.</p>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
                            <div><strong>Base ICMS:</strong> {moeda(nota.baseCalculoIcms)}</div>
                            <div><strong>ICMS:</strong> {moeda(nota.valorIcms)}</div>
                            <div><strong>PIS:</strong> {moeda(nota.valorPis)}</div>
                            <div><strong>COFINS:</strong> {moeda(nota.valorCofins)}</div>
                            <div><strong>IPI:</strong> {moeda(nota.valorIpi)}</div>
                            <div><strong>DIFAL destino:</strong> {moeda(nota.valorIcmsUfDestino)}</div>
                            <div><strong>DIFAL origem:</strong> {moeda(nota.valorIcmsUfOrigem)}</div>
                            <div><strong>FCP:</strong> {moeda(nota.valorFcp)}</div>
                            <div><strong>FCP destino:</strong> {moeda(nota.valorFcpUfDestino)}</div>
                            <div><strong>Base IBS/CBS:</strong> {moeda(nota.baseCalculoIbsCbs)}</div>
                            <div><strong>IBS estadual:</strong> {moeda(nota.valorIbsEstadual)}</div>
                            <div><strong>IBS municipal:</strong> {moeda(nota.valorIbsMunicipal)}</div>
                            <div><strong>IBS total:</strong> {moeda(nota.valorIbs)}</div>
                            <div><strong>CBS:</strong> {moeda(nota.valorCbs)}</div>
                        </div>
                        <DataTable value={nota.itens ?? []} emptyMessage="Sem itens" responsiveLayout="scroll">
                            <Column field="numeroItem" header="#" /><Column field="descricaoProduto" header="Produto" />
                            <Column header="Transação" body={(item: NotaFiscalSaidaItem) => item.transacaoNotaFiscal?.descricao ?? "—"} />
                            <Column header="Origem da transação" body={(item: NotaFiscalSaidaItem) => item.herdaTransacaoNota === false ? "Própria do item" : "Herdada da nota"} />
                            <Column field="cfop" header="CFOP" />
                            <Column header="Base ICMS" body={(item: NotaFiscalSaidaItem) => moeda(item.baseCalculoIcms)} />
                            <Column header="ICMS" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIcms)} />
                            <Column header="Diferimento ICMS (%)" body={(item: NotaFiscalSaidaItem) => item.cstIcms === "51"
                                ? item.percentualDiferimentoIcms?.toLocaleString("pt-BR", { maximumFractionDigits: 4 }) : "—"} />
                            <Column header="ICMS da operação" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIcmsOperacao)} />
                            <Column header="ICMS diferido" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIcmsDiferido)} />
                            <Column header="PIS" body={(item: NotaFiscalSaidaItem) => moeda(item.valorPis)} />
                            <Column header="COFINS" body={(item: NotaFiscalSaidaItem) => moeda(item.valorCofins)} />
                            <Column header="IPI" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIpi)} />
                            <Column header="DIFAL destino" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIcmsUfDestino)} />
                            <Column header="FCP" body={(item: NotaFiscalSaidaItem) => moeda(item.valorFcp)} />
                            <Column header="FCP destino" body={(item: NotaFiscalSaidaItem) => moeda(item.valorFcpUfDestino)} />
                            <Column header="IBS" body={(item: NotaFiscalSaidaItem) => moeda(item.valorIbs)} />
                            <Column header="CBS" body={(item: NotaFiscalSaidaItem) => moeda(item.valorCbs)} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Documentos XML">
                        <NotaFiscalSaidaDocumentos idNota={nota.id} />
                    </TabPanel>
                    <TabPanel header={`Itens (${nota.itens?.length ?? 0})`}>
                        <DataTable value={nota.itens ?? []} emptyMessage="Sem itens" responsiveLayout="scroll">
                            <Column field="numeroItem" header="#" /><Column field="codigoProdutoFiscal" header="Código" />
                            <Column field="descricaoProduto" header="Produto" /><Column field="cfop" header="CFOP" />
                            <Column field="ncm" header="NCM" /><Column field="quantidadeComercial" header="Quantidade" />
                            <Column field="valorUnitario" header="Unitário" body={item => moeda(item.valorUnitario)} />
                            <Column field="valorTotalItem" header="Total" body={item => moeda(item.valorTotalItem)} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header={`Pagamentos (${nota.pagamentos?.length ?? 0})`}>
                        <DataTable value={nota.pagamentos ?? []} emptyMessage="Sem pagamentos">
                            <Column field="sequencia" header="#" /><Column field="formaPagamento.descricao" header="Forma" />
                            <Column field="tipoPagamento" header="Tipo" />
                            <Column field="valorPagamento" header="Valor" body={pagamento => moeda(pagamento.valorPagamento)} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header={`Parcelas (${nota.parcelas?.length ?? 0})`}>
                        <DataTable value={nota.parcelas ?? []} emptyMessage="Sem parcelas">
                            <Column field="numeroParcela" header="Parcela" />
                            <Column field="dataVencimento" header="Vencimento"
                                body={(parcela: NotaFiscalSaidaParcela) => parcela.dataVencimento
                                    ? converterData(new Date(`${parcela.dataVencimento}T00:00:00`))
                                    : "—"} />
                            <Column field="valorParcela" header="Valor" body={parcela => moeda(parcela.valorParcela)} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header={`Volumes (${nota.volumes?.length ?? 0})`}>
                        <DataTable value={nota.volumes ?? []} emptyMessage="Sem volumes">
                            <Column field="quantidadeVolumes" header="Quantidade" /><Column field="especie" header="Espécie" />
                            <Column field="marca" header="Marca" /><Column field="numeracao" header="Numeração" />
                            <Column field="pesoLiquido" header="Peso líquido" /><Column field="pesoBruto" header="Peso bruto" />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header={`Referências (${nota.notasReferenciadas?.length ?? 0})`}>
                        <DataTable value={nota.notasReferenciadas ?? []} emptyMessage="Sem notas referenciadas">
                            <Column field="tipoReferencia" header="Tipo" /><Column field="chaveAcesso" header="Chave de acesso" />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Histórico fiscal"><NotaFiscalSaidaHistoricoFiscal id={nota.id} /></TabPanel>
                    <TabPanel header="E-mails"><NotaFiscalSaidaEmails key={nota.id} nota={nota} podeGerenciar={podeAtualizarDados} /></TabPanel>
                </TabView>
                {nota.informacoesComplementares && <p><strong>Informações complementares:</strong> {nota.informacoesComplementares}</p>}
            </>}
        </div>
    </Base>;
}
